import styled, { css } from 'particles/themes/styled'
import { TagState, TagContentType } from './index'
import { Theme } from 'particles/themes'

const getTagBackgroundColor = (state: TagState, contentType: TagContentType, theme: Theme) => {
    if (state === 'hover') {
        if (contentType === 'positive') {
            return theme.colors.surface.positive
        } else if (contentType === 'warning') {
            return theme.colors.surface.warningActive
        } else if (contentType === 'negative') {
            return theme.colors.surface.error
        } else {
            return theme.colors.surface.secondaryInverse
        }
    } else if (state === 'disable') {
        return theme.colors.surface.disable
    } else {
        if (contentType === 'positive') {
            return theme.colors.surface.positivePastel
        } else if (contentType === 'warning') {
            return theme.colors.surface.warningPastel
        } else if (contentType === 'negative') {
            return theme.colors.surface.errorPastel
        } else {
            return theme.colors.surface.tertiary
        }
    }
}

export const getTagFontColor = (state: TagState, contentType: TagContentType, theme: Theme) => {
    if (state === 'hover') {
        return theme.colors.text.primaryInverse
    } else if (state === 'disable') {
        return theme.colors.text.disable
    } else {
        if (contentType === 'positive') {
            return theme.colors.text.positive
        } else if (contentType === 'warning') {
            return theme.colors.text.warning
        } else if (contentType === 'negative') {
            return theme.colors.text.error
        } else {
            return theme.colors.text.primary
        }
    }
}

export interface StyledTagProps {
    label: string
    state: TagState
    contentType: TagContentType
    maxWidth: string
    customStyle?: React.CSSProperties
    hasNumber: boolean
    isHoverable: boolean
}

export const StyledTag = styled.button<StyledTagProps>`
    font-family: inherit;
    background: ${({ state, contentType, disabled, theme, customStyle }) =>
        customStyle?.backgroundColor || getTagBackgroundColor(disabled ? 'disable' : state, contentType, theme)};
    color: ${({ state, disabled, theme, customStyle, contentType }) =>
        customStyle?.color || getTagFontColor(disabled ? 'disable' : state, contentType, theme)};
    padding: 0px;
    padding-left: 6px;
    padding-right: ${({ hasNumber }) => (hasNumber ? '2px' : '6px')};
    height: 20px;
    max-width: ${({ maxWidth }) => maxWidth};
    min-width: ${({ customStyle }) => customStyle?.minWidth || 'unset'};
    width: fit-content;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[100]};
    border-radius: 30px;
    box-shadow: 0px 1px 8px 0px rgba(0, 0, 0, 0.06);
    border: ${({ customStyle }) => (customStyle?.borderColor ? `1px solid ${customStyle.borderColor}` : 0)};
    // Apply styles from the customCss prop
    ${props =>
        props.customStyle &&
        css`
            ${Object.entries(props.customStyle)
                .map(([key, value]) => {
                    // Convert camelCase to kebab-case for CSS properties
                    const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase()
                    return `${cssKey}: ${value};`
                })
                .join('\n')}
        `}

    .tag-icon {
        min-width: 16px;
    }

    .label {
        color: ${({ state, theme, customStyle, contentType }) =>
            customStyle?.color || getTagFontColor(state, contentType, theme)};
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
    }

    :disabled .label {
        color: ${({ theme, contentType }) => getTagFontColor('disable', contentType, theme)};
    }

    .number {
        color: ${({ state, theme, contentType }) =>
            getTagFontColor(state === 'hover' ? 'default' : state, contentType, theme)};
        min-width: 16px;
        padding: 0px 4px 2px 4px;
        height: 16px;
        background: #ffffffcc;
        border-radius: 12px;
        flex-shrink: 0;
        box-sizing: border-box;
    }

    :disabled .number {
        color: ${({ theme, contentType }) => getTagFontColor('disable', contentType, theme)};
    }

    ${({ isHoverable, contentType, theme, customStyle }) =>
        isHoverable
            ? `&:hover:not([disabled]),
            &:hover:not([disabled]) .label {
                cursor: pointer;
                background: ${customStyle?.color || getTagBackgroundColor('hover', contentType, theme)};
                color: ${customStyle?.background || getTagFontColor('hover', contentType, theme)};`
            : ''}
`

export interface StyledTextTagProps {
    label: string
    contentType: TagContentType
    maxWidth: string
}

export const StyledTextTag = styled.button<StyledTextTagProps>`
    font-family: inherit;
    background: ${({ contentType, theme }) => getTagBackgroundColor('hover', contentType, theme)};
    color: ${({ theme, contentType }) => getTagFontColor('hover', contentType, theme)};
    padding: 0px 6px;
    height: 20px;
    max-width: ${({ maxWidth }) => maxWidth};
    display: flex;
    justify-content: center;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[100]};
    border-radius: 2px;
    border: 0;

    .tag-icon {
        min-width: 16px;
    }

    .label {
        color: ${({ theme, contentType }) => getTagFontColor('hover', contentType, theme)};
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
    }

    .number {
        color: ${({ theme, contentType }) => getTagFontColor('hover', contentType, theme)};
    }
`
